/**
 * Electron bridge — typed wrapper around the API exposed in preload.js.
 *
 * Safe to import from anywhere in the app. In the browser / mobile build
 * `window.electronAPI` is undefined, so every export becomes a no-op and
 * `isElectron` is `false`.
 */

export interface DesktopPrefs {
  notificationSound: boolean;
  autoLaunch: boolean;
}

/** What the user did on the incoming-call popup window. 'accept' / 'decline'
 *  = the popup's buttons (main has already focused the window on accept);
 *  'focus' = a click on the popup body (main focused the window, popup stays
 *  up); 'dismiss' is legacy from the OS-notification era — never sent by the
 *  popup, and must never be treated as a decline. */
export type CallNotificationAction = "accept" | "decline" | "focus" | "dismiss";

export interface ElectronAPI {
  isElectron: true;
  platform: NodeJS.Platform;
  showWindow: () => Promise<void>;
  quit: () => Promise<void>;
  getVersion: () => Promise<string>;
  /** Stable per-installation UUID from <userData>/config.json. Optional for
   *  older preloads, which predate single-session enforcement. */
  getDeviceId?: () => Promise<string>;
  getPrefs: () => Promise<DesktopPrefs>;
  setPrefs: (partial: Partial<DesktopPrefs>) => Promise<void>;
  onPrefsChanged: (cb: (prefs: DesktopPrefs) => void) => () => void;
  showNotification: (opts: {
    title?: string;
    body?: string;
    silent?: boolean;
    conversationId?: string | null;
    /** Remote avatar URL; main process downloads it into the notification
     *  icon (Windows/Linux). Falls back to the app icon if absent or on
     *  failure. macOS always uses the app icon (OS restriction). */
    iconUrl?: string | null;
  }) => Promise<void>;
  onNotificationClick: (
    cb: (info: { conversationId: string }) => void,
  ) => () => void;
  /** Incoming-call popup — an always-on-top window with the caller's photo
   *  and Accept/Decline that stays up while the call rings. Re-invoking for
   *  the same callId patches the live popup in place (late avatar).
   *  Optional for older preloads. */
  showCallNotification?: (opts: {
    callId: string;
    title?: string;
    body?: string;
    iconUrl?: string | null;
  }) => Promise<void>;
  /** Withdraw the popup for a call that resolved (answered, declined,
   *  cancelled, timed out). Optional for older preloads. */
  closeCallNotification?: (callId: string) => Promise<void>;
  onCallNotificationAction?: (
    cb: (info: { callId: string; action: CallNotificationAction }) => void,
  ) => () => void;
  /** OS ringtone audio (base64) or null when the platform has none to offer.
   *  Optional for older preloads. */
  getSystemRingtone?: () => Promise<{ data: string; mime: string } | null>;
  onUpdateAvailable: (cb: (info: { version: string }) => void) => () => void;
  onUpdateDownloaded: (cb: (info: { version: string }) => void) => () => void;
  windowMinimize: () => Promise<void>;
  windowToggleMaximize: () => Promise<boolean>;
  windowClose: () => Promise<void>;
  windowIsMaximized: () => Promise<boolean>;
  onWindowMaximizedChange: (cb: (isMaximized: boolean) => void) => () => void;
  onWindowVisibilityChange: (
    cb: (visibility: "visible" | "hidden") => void,
  ) => () => void;
  /** One-shot pull of the CURRENT window visibility (main is the source of
   *  truth). Seeds presence after a reload that happened while hidden, where
   *  no transition event would otherwise fire. Optional for older preloads. */
  getWindowVisibility?: () => Promise<"visible" | "hidden">;
  logPresence: (msg: string) => void;
}

declare global {
  interface Window {
    electronAPI?: ElectronAPI;
  }
}

export const isElectron: boolean =
  typeof window !== "undefined" && window.electronAPI?.isElectron === true;

export const desktop = window?.electronAPI;

// Desktop window-chrome flags. macOS keeps its native traffic-light buttons;
// every other desktop platform draws custom min/max/close controls in the
// renderer (see WindowTitlebar).
export const isMacDesktop: boolean = isElectron && desktop?.platform === "darwin";
export const usesCustomWindowControls: boolean =
  isElectron && desktop?.platform !== "darwin";

export function minimizeWindow(): void {
  desktop?.windowMinimize().catch(() => {});
}

export function toggleMaximizeWindow(): void {
  desktop?.windowToggleMaximize().catch(() => {});
}

export function closeWindow(): void {
  desktop?.windowClose().catch(() => {});
}

export async function showDesktopNotification(opts: {
  title?: string;
  body?: string;
  silent?: boolean;
  conversationId?: string | null;
  iconUrl?: string | null;
}): Promise<void> {
  if (!desktop) return;
  try {
    await desktop.showNotification(opts);
  } catch {
    /* swallow — notifications are best-effort */
  }
}

export function onDesktopNotificationClick(
  cb: (info: { conversationId: string }) => void,
): () => void {
  if (!desktop) return () => {};
  return desktop.onNotificationClick(cb);
}

export async function showCallNotification(opts: {
  callId: string;
  title?: string;
  body?: string;
  iconUrl?: string | null;
}): Promise<void> {
  if (!desktop?.showCallNotification) return;
  try {
    await desktop.showCallNotification(opts);
  } catch {
    /* swallow — notifications are best-effort */
  }
}

export async function closeCallNotification(callId: string): Promise<void> {
  if (!desktop?.closeCallNotification) return;
  try {
    await desktop.closeCallNotification(callId);
  } catch {
    /* swallow — notifications are best-effort */
  }
}

export function onCallNotificationAction(
  cb: (info: { callId: string; action: CallNotificationAction }) => void,
): () => void {
  if (!desktop?.onCallNotificationAction) return () => {};
  return desktop.onCallNotificationAction(cb);
}

export async function getSystemRingtone(): Promise<{
  data: string;
  mime: string;
} | null> {
  if (!desktop?.getSystemRingtone) return null;
  try {
    return await desktop.getSystemRingtone();
  } catch {
    return null;
  }
}

export async function getDesktopPrefs(): Promise<DesktopPrefs | null> {
  if (!desktop) return null;
  try {
    return await desktop.getPrefs();
  } catch {
    return null;
  }
}

export async function setDesktopPrefs(
  partial: Partial<DesktopPrefs>,
): Promise<void> {
  if (!desktop) return;
  try {
    await desktop.setPrefs(partial);
  } catch {
    /* no-op */
  }
}

/**
 * The desktop installation's stable device id, or null anywhere else.
 *
 * Null is a normal answer, not an error: in the browser build, and on an
 * older preload that has no `getDeviceId`, the caller falls back to a
 * localStorage id. Never throws — this sits on the login path.
 */
export async function getDesktopDeviceId(): Promise<string | null> {
  if (!desktop?.getDeviceId) return null;
  try {
    return (await desktop.getDeviceId()) || null;
  } catch {
    return null;
  }
}
